import { z } from 'zod';

// Base object schema with all fields
export const objectFormSchema = z.object({
    // Object Details Tab
    ObjectService: z.enum(['Object', 'Service']).default('Object'),
    
    ObjectID: z.string()
        .min(1, 'Object ID is required')
        .max(250, 'Object ID must be less than 250 characters')
        .regex(/^[A-Za-z0-9-]+$/, {
            message: 'Object ID must only contain letters, numbers and hyphens',
        }),
    
    Description: z.string()
        .max(1000, 'Description must be less than 1000 characters')
        .nullable()
        .optional(),
    
    // The classification is the object's band. It replaced the LayerID ladder
    // outright: the form neither collects nor submits LayerID any more, so the
    // API is free to derive that legacy column from the classification.
    ClassificationID: z.union([
        z.number().int().positive({ message: 'Classification is required' }),
        z.null(),
        z.undefined()
    ]),

    // 0 is the database's "unset" sentinel for these (the API writes 0 back for
    // null), so it must round-trip through the form rather than fail validation.
    ResponsibleTeam: z.number().int().min(0).nullable().optional(),
    
    RPO: z.union([
        z.string(), // Dropdown value
        z.number().int().min(0) // Minutes entered directly
    ]).nullable().optional(),
    
    RTO: z.union([
        z.string(), // Dropdown value
        z.number().int().min(0) // Minutes entered directly
    ]).nullable().optional(),
    
    Location: z.number().int().positive().nullable().optional(),
    
    ManagementOnly: z.number().int().min(0).max(1).default(0),
    // Tri-state. null = inherit the classification's setting, 1 = force shared,
    // 0 = force not-shared. nullable rather than defaulting to 0, because a
    // default of 0 would mean every object silently overrides its class.
    SharedServiceOverride: z.number().int().min(0).max(1).nullable().default(null),
    
    BackupSolution: z.union([
        z.number().int().positive(),
        z.literal(0),
        z.null()
    ]).optional(),
    
    Datastore: z.number().int().min(0).max(1).default(0),
    
    ApplicationEncryption: z.number().int().min(0).max(1).default(0),

    // Retention in days — objects are capped at 30 days
    Retention: z.union([
        z.number().int().min(1, 'Retention must be at least 1 day').max(30, 'Retention cannot exceed 30 days'),
        z.null(),
        z.undefined()
    ]).optional(),

    Analytics: z.enum(['Yes', 'No']).default('No'),
    
    Icon: z.string().nullable().optional(), // Placeholder for image server
    
    Technology: z.number().int().positive().nullable().optional(),
    
    // Floor Plan Tab (placeholder)
    FloorPlanImage: z.string().nullable().optional(),
    FloorPlanMarker: z.any().nullable().optional(),
    
    // Recovery Information Tab
    Instructions: z.string().nullable().optional(), // HTML content
    
    // Recovery Document/Video Tab
    RecoveryDocument: z.number().int().positive().nullable().optional(),
    RecoveryVideo: z.number().int().positive().nullable().optional(),
    
    // Runbook Tab
    MasterRunbook: z.number().int().positive().nullable().optional(),
    RunbookTasks: z.array(z.object({
        id: z.number().int().nullable().optional(),
        taskNo: z.number().int().nullable(),
        task: z.string(),
        commands: z.string(),
        duration: z.number().int().nullable(),
        taskOffset: z.number().int().nullable().optional(), // Minutes from runbook start
        startTime: z.string().nullable().optional(), // Deprecated: calculated from taskOffset for display
        finishTime: z.string().nullable().optional(), // Deprecated: calculated from taskOffset for display
        dependencies: z.string(),
    })).optional(),
    
    // Object Picture Tab
    ObjectImage: z.string().nullable().optional(), // Placeholder for image server
});

// An object has a location; a service may have one. Andrew, 9 Oct 2026: "make location mandatory
// in an object. Business services are different. Some will not have a location." The server
// enforces it (backend/src/lib/object-create.js); this is so the form says so before sending.
const objectLocationValidation = (data: any) => data.ObjectService === 'Service' || (typeof data.Location === 'number' && data.Location > 0);
const OBJECT_LOCATION_ISSUE = { message: 'Location is required for an object', path: ['Location'] };

// Validation function for BackupSolution
const backupSolutionValidation = (data: any) => {
    // If ManagementOnly is 0, BackupSolution must be 0 or null
    if (data.ManagementOnly === 0 && data.BackupSolution !== null && data.BackupSolution !== 0) {
        return false;
    }
    return true;
};

export const createObjectSchema = objectFormSchema
    .refine(backupSolutionValidation, {
        message: 'Backup Solution can only be set when Management Only is enabled',
        path: ['BackupSolution'],
    })
    .refine((data) => {
        // Every object AND every service must be classified — the band it
        // draws on and the rules that govern its mappings both come from it.
        if (data.ClassificationID === null || data.ClassificationID === undefined) {
            return false;
        }
        return true;
    }, {
        message: 'Classification is required',
        path: ['ClassificationID'],
    })
    .refine(objectLocationValidation, OBJECT_LOCATION_ISSUE)
    .refine((data) => {
        // When ObjectService is 'Service', certain fields must be null
        if (data.ObjectService === 'Service') {
            // These fields should be null for services
            return true; // Validation passed, we'll set them to null in handleSubmit
        }
        return true;
    });

export const updateObjectSchema = objectFormSchema
    .partial()
    .extend({
        ObjectID: z.string().optional(), // ObjectID can't be changed after creation
    })
    .refine((data) => {
        // Only validate if ManagementOnly and BackupSolution are provided
        if (data.ManagementOnly !== undefined && data.BackupSolution !== undefined) {
            return backupSolutionValidation(data);
        }
        return true;
    }, {
        message: 'Backup Solution can only be set when Management Only is enabled',
        path: ['BackupSolution'],
    })
    // The edit form always carries ObjectService; a partial update that says nothing about it is
    // not asked for a location here (the server decides, from what the object is).
    .refine((data) => data.ObjectService === undefined || objectLocationValidation(data), OBJECT_LOCATION_ISSUE);

export type ObjectFormData = z.infer<typeof objectFormSchema>;
export type CreateObjectFormData = z.infer<typeof createObjectSchema>;
export type UpdateObjectFormData = z.infer<typeof updateObjectSchema>;
